import React, { useState, useEffect, useContext, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Chip from "@material-ui/core/Chip";
import Switch from "@material-ui/core/Switch";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContactIdentificador from "../ContactIdentificador";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import WelcomeNoticeContactRow from "../WelcomeNotice/WelcomeNoticeContactRow";
import DoNotDisturbContactRow from "../DoNotDisturb/DoNotDisturbContactRow";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: `1px solid ${theme.palette.divider}`,
		borderRight: `1px solid ${theme.palette.divider}`,
		borderBottom: `1px solid ${theme.palette.divider}`,
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
		backgroundColor: theme.palette.background.paper,
	},
	header: {
		display: "flex",
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.background.default,
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: 4,
		},
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
	notesSection: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
	},
	noteItem: {
		marginTop: 4,
		padding: 6,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
	},
	noteText: {
		flex: 1,
		wordBreak: "break-word",
	},
	noteMeta: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		marginTop: 2,
	},
	noteInput: {
		marginTop: 8,
	},
	timelineItem: {
		marginBottom: 8,
		padding: 8,
	},
	timelineTicket: {
		borderLeft: "3px solid #10b981",
	},
	timelineNote: {
		borderLeft: "3px solid #f59e0b",
	},
	timelineMeta: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		marginTop: 2,
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);

	const [modalOpen, setModalOpen] = useState(false);
	const [notes, setNotes] = useState([]);
	const [newNote, setNewNote] = useState("");
	const [contactTypes, setContactTypes] = useState([]);
	const [selectedTypeId, setSelectedTypeId] = useState("");
	const [activeTab, setActiveTab] = useState(0);
	const [tickets, setTickets] = useState([]);
	const [contactTags, setContactTags] = useState([]);
	const [allTags, setAllTags] = useState([]);
	const [showTagSelector, setShowTagSelector] = useState(false);

	useEffect(() => {
		if (!open || !contact?.id) return;
		api.get(`/contacts/${contact.id}/notes`)
			.then(({ data }) => setNotes(data))
			.catch(err => toastError(err));
		setSelectedTypeId(contact.contactTypeId || "");
		// Fresh fetch para obtener tags actualizados (no depender de prop stale)
		api.get(`/contacts/${contact.id}`)
			.then(({ data }) => setContactTags(data.tags || []))
			.catch(() => setContactTags(contact.tags || []));
	}, [open, contact?.id, contact?.contactTypeId]);

	useEffect(() => {
		if (!open) return;
		api.get("/contact-types").then(({ data }) => setContactTypes(data)).catch(() => {});
		api.get("/tags").then(({ data }) => setAllTags(data)).catch(() => {});
	}, [open]);

	const handleAddContactTag = useCallback(async (tagId) => {
		try {
			const { data } = await api.post(`/contacts/${contact.id}/tags/${tagId}`);
			setContactTags(data);
			setShowTagSelector(false);
		} catch (err) {
			if (err?.response?.status === 409) {
				toast.warning("Este tag ya está asignado al contacto");
			} else {
				toastError(err);
			}
		}
	}, [contact?.id]);

	const handleRemoveContactTag = useCallback(async (tagId) => {
		try {
			const { data } = await api.delete(`/contacts/${contact.id}/tags/${tagId}`);
			setContactTags(data);
		} catch (err) {
			toastError(err);
		}
	}, [contact?.id]);

	useEffect(() => {
		if (!open || !contact?.id || activeTab !== 1) return;
		api.get(`/tickets`, { params: { contactId: contact.id, pageNumber: 1 } })
			.then(({ data }) => setTickets(data.tickets || []))
			.catch(() => {});
	}, [open, contact?.id, activeTab]);

	const handleSaveType = useCallback(async (typeId) => {
		if (!contact?.number) return;
		try {
			await api.put(`/contacts/phone/${contact.number}/type`, { contactTypeId: typeId || null });
			toast.success("Tipo de cliente actualizado");
		} catch (err) {
			toastError(err);
		}
	}, [contact?.number]);

	const handleToggleAiBlocked = async () => {
		try {
			await api.put(`/contacts/${contact.id}/intervention`, {
				aiBlocked: !contact.aiBlocked,
			});
			// El socket "contact" { action:"update" } ya actualiza el estado en Ticket/index.js
		} catch (err) {
			toastError(err);
		}
	};

	const handleAddNote = async () => {
		if (!newNote.trim()) return;
		try {
			const { data } = await api.post(`/contacts/${contact.id}/notes`, { note: newNote });
			setNotes(prev => [data, ...prev]);
			setNewNote("");
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteNote = async (noteId) => {
		try {
			await api.delete(`/contacts/${contact.id}/notes/${noteId}`);
			setNotes(prev => prev.filter(n => n.id !== noteId));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
				<Tabs
					value={activeTab}
					onChange={(_, v) => setActiveTab(v)}
					indicatorColor="primary"
					textColor="primary"
					variant="fullWidth"
					style={{ borderBottom: "1px solid rgba(0,0,0,0.12)", backgroundColor: "white", flexShrink: 0 }}
				>
					<Tab label="Info" style={{ minHeight: 40, fontSize: 12 }} />
					<Tab label="Timeline" style={{ minHeight: 40, fontSize: 12 }} />
				</Tabs>

				{activeTab === 1 ? (
					<div style={{ padding: 8 }}>
						{/* Resumen IA — primer bloque de la timeline si existe */}
						{contact?.aiSummary && (
							<Paper square variant="outlined" style={{
								marginBottom: 12,
								padding: 8,
								backgroundColor: "#fffde7",
								borderLeft: "3px solid #ffc107",
							}}>
								<Typography variant="caption" style={{
									display: "flex", alignItems: "center", gap: 4,
									fontWeight: 600, color: "rgba(0,0,0,0.54)", marginBottom: 4,
								}}>
									🤖 Resumen IA
								</Typography>
								<Typography variant="body2" style={{
									whiteSpace: "pre-wrap", wordBreak: "break-word",
									maxHeight: 200, overflowY: "auto",
								}}>
									{contact.aiSummary}
								</Typography>
							</Paper>
						)}
						<Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
							Historial del contacto
						</Typography>
						{tickets.length === 0 && notes.length === 0 && (
							<Typography variant="body2" color="textSecondary">Sin actividad registrada</Typography>
						)}
						{[
							...tickets.map(t => ({ type: "ticket", date: t.createdAt, data: t })),
							...notes.map(n => ({ type: "note", date: n.createdAt, data: n }))
						].sort((a, b) => new Date(b.date) - new Date(a.date)).map((item, idx) => (
							<Paper key={idx} square variant="outlined"
								className={classes.timelineItem}
								style={{ borderLeft: item.type === "ticket" ? "3px solid #10b981" : "3px solid #f59e0b", marginBottom: 8, padding: 8 }}
							>
								{item.type === "ticket" ? (
									<>
										<div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
											<Chip label={item.data.status} size="small"
												style={{ fontSize: 10, height: 18,
													backgroundColor: item.data.status === "closed" ? "#10b981" : item.data.status === "open" ? "#3b82f6" : "#f59e0b",
													color: "#fff" }}
											/>
											{item.data.contactReason?.name && (
												<Chip label={item.data.contactReason.name} size="small"
													style={{ fontSize: 10, height: 18, backgroundColor: "#667eea", color: "#fff" }} />
											)}
										</div>
										<Typography className={classes.timelineMeta}>
											Ticket #{item.data.id} · {item.data.createdAt ? format(parseISO(item.data.createdAt), "dd/MM/yy HH:mm") : ""}
										</Typography>
									</>
								) : (
									<>
										<Typography variant="body2">{item.data.note}</Typography>
										<Typography className={classes.timelineMeta}>
											{item.data.user?.name} · {item.data.createdAt ? format(parseISO(item.data.createdAt), "dd/MM/yy HH:mm") : ""}
										</Typography>
									</>
								)}
							</Paper>
						))}
					</div>
				) : (
				<>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={contact.name}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography>{contact.name}</Typography>
						<ContactIdentificador number={contact.number} size="wide" />
						<Typography>
							<Link href={`tel:${contact.number}`}>{contact.number}</Link>
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => setModalOpen(true)}
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
						{contactTypes.length > 0 && (
							<FormControl size="small" variant="outlined" style={{ marginTop: 8, minWidth: 180 }}>
								<InputLabel>Tipo de cliente</InputLabel>
								<Select
									value={selectedTypeId}
									onChange={e => { setSelectedTypeId(e.target.value); handleSaveType(e.target.value); }}
									label="Tipo de cliente"
								>
									<MenuItem value=""><em>Sin tipo</em></MenuItem>
									{contactTypes.map(t => (
										<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					</Paper>
					<Paper square variant="outlined" className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						{/* Datos de perfil del contacto */}
						{contact?.lastName && (
							<Paper square variant="outlined" className={classes.contactExtraInfo}>
								<InputLabel>Apellido</InputLabel>
								<Typography variant="body2" style={{ paddingTop: 2 }}>{contact.lastName}</Typography>
							</Paper>
						)}
						{contact?.organization && (
							<Paper square variant="outlined" className={classes.contactExtraInfo}>
								<InputLabel>Empresa</InputLabel>
								<Typography variant="body2" style={{ paddingTop: 2 }}>{contact.organization}</Typography>
							</Paper>
						)}
						{contact?.notes && (
							<Paper square variant="outlined" className={classes.contactExtraInfo}>
								<InputLabel>Notas</InputLabel>
								<Typography variant="body2" style={{ paddingTop: 2, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{contact.notes}</Typography>
							</Paper>
						)}
						<Typography variant="subtitle1" style={{ marginTop: contact?.lastName || contact?.organization || contact?.notes ? 8 : 0 }}>
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{contact?.extraInfo?.map(info => (
							<Paper
								key={info.id}
								square
								variant="outlined"
								className={classes.contactExtraInfo}
							>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" noWrap style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
					</Paper>

					{/* ── Resumen IA ── */}
				{contact?.aiSummary && (
					<Paper square variant="outlined" style={{ marginTop: 8, padding: 8 }}>
						<Typography variant="subtitle2" style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
							🤖 Resumen IA
						</Typography>
						<Typography variant="body2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
							{contact.aiSummary}
						</Typography>
					</Paper>
				)}

				{/* ── Bloquear IA ── */}
				<Paper square variant="outlined" style={{ padding: "8px 16px", marginTop: 8 }}>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<div>
							<Typography variant="subtitle2">Bloquear IA</Typography>
							<Typography variant="caption" color="textSecondary">
								Este contacto siempre será atendido por un asesor humano
							</Typography>
						</div>
						<Switch
							checked={contact?.aiBlocked || false}
							onChange={handleToggleAiBlocked}
							color="secondary"
						/>
					</div>
				</Paper>

				{/* ── feat_420: No molestar (no recibe campañas) ── */}
				<DoNotDisturbContactRow contact={contact} />

				{/* ── feat_416: mensaje de bienvenida una sola vez ── */}
				<WelcomeNoticeContactRow contact={contact} />

				{/* ── Etiquetas del contacto ── */}
				<Paper square variant="outlined" style={{ padding: "8px 16px", marginTop: 8 }}>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
						<Typography variant="subtitle2">Etiquetas</Typography>
						<IconButton size="small" onClick={() => setShowTagSelector(s => !s)} title="Agregar etiqueta">
							<span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>+</span>
						</IconButton>
					</div>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
						{contactTags.map(tag => (
							<Chip
								key={tag.id}
								label={tag.name}
								size="small"
								onDelete={() => handleRemoveContactTag(tag.id)}
								style={{ backgroundColor: tag.color || "#6c757d", color: "#fff", fontSize: 11 }}
							/>
						))}
						{contactTags.length === 0 && !showTagSelector && (
							<Typography variant="caption" color="textSecondary">Sin etiquetas</Typography>
						)}
					</div>
					{showTagSelector && (
						<div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
							{allTags
								.filter(t => !contactTags.some(ct => ct.id === t.id))
								.map(tag => (
									<Chip
										key={tag.id}
										label={tag.name}
										size="small"
										clickable
										onClick={() => handleAddContactTag(tag.id)}
										style={{ backgroundColor: tag.color || "#6c757d", color: "#fff", fontSize: 11, cursor: "pointer" }}
									/>
								))}
							{allTags.filter(t => !contactTags.some(ct => ct.id === t.id)).length === 0 && (
								<Typography variant="caption" color="textSecondary">Todas las etiquetas ya están asignadas</Typography>
							)}
						</div>
					)}
				</Paper>

				{/* ── Notas internas ── */}
					<Paper square variant="outlined" className={classes.notesSection}>
						<Typography variant="subtitle1">Notas internas</Typography>
						<Divider style={{ margin: "4px 0 8px" }} />

						{notes.map(n => (
							<Paper key={n.id} square variant="outlined" className={classes.noteItem}>
								<div className={classes.noteText}>
									<Typography variant="body2">{n.note}</Typography>
									<Typography className={classes.noteMeta}>
										{n.user?.name} · {n.createdAt ? format(parseISO(n.createdAt), "dd/MM/yy HH:mm") : ""}
									</Typography>
								</div>
								{n.userId === user?.id && (
									<IconButton size="small" onClick={() => handleDeleteNote(n.id)}>
										<DeleteIcon fontSize="small" />
									</IconButton>
								)}
							</Paper>
						))}

						{notes.length === 0 && (
							<Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
								Sin notas
							</Typography>
						)}

						<TextField
							className={classes.noteInput}
							label="Nueva nota"
							multiline
							rows={2}
							fullWidth
							variant="outlined"
							size="small"
							value={newNote}
							onChange={e => setNewNote(e.target.value)}
						/>
						<Button
							style={{ marginTop: 6 }}
							size="small"
							variant="contained"
							color="primary"
							onClick={handleAddNote}
							disabled={!newNote.trim()}
						>
							Agregar nota
						</Button>
					</Paper>
			</>
			)}
			</div>
		)}
	</Drawer>
	);
};

export default ContactDrawer;
